<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <b-form ref="form" lazy-validation class="row">
              <b-container class="bv-example-row">
                <!-- Khối 1: Thông tin chung -->
                <b-row>
                  <b-col cols="6">
                    <b-form-group class="required-control">
                      <label>Tên nhóm</label>
                      <b-form-input
                        size="sm"
                        v-model="$v.mainModel.name.$model"
                        :state="validateState('name')"
                        placeholder="Nhập tên nhóm hoa hồng"
                      ></b-form-input>
                      <b-form-invalid-feedback
                        >Vui lòng nhập tên nhóm</b-form-invalid-feedback
                      >
                    </b-form-group>
                    <b-form-group>
                      <label>Mô tả</label>
                      <b-form-textarea
                        v-model="mainModel.description"
                        placeholder="Nhập mô tả"
                        rows="5"
                        :max-length="255"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-col cols="8" class="pl-0">
                      <b-form-group class="required-control">
                        <label>Loại nhóm</label>
                        <b-form-select
                          size="sm"
                          v-model="mainModel.type"
                          :options="typeOptions"
                          value-field="id"
                          text-field="name"
                        ></b-form-select>
                        <span
                          v-if="!isProductType"
                          class="text-muted d-block mt-2"
                        >
                          Nhóm này chỉ gồm tên và mô tả, không cần chọn sản phẩm
                          / danh mục.
                        </span>
                      </b-form-group>
                    </b-col>
                  </b-col>
                </b-row>

                <!-- Khối 2: Danh sách sản phẩm / danh mục -->
                <template v-if="isProductType">
                  <h4>Danh sách sản phẩm / danh mục</h4>
                  <div class="separator separator-dashed my-5"></div>
                  <b-row>
                    <b-col md="12" class="pr-0">
                      <b-form-group>
                        <label>Tìm sản phẩm / danh mục:</label>
                        <b-row>
                          <b-col md="9">
                            <CommissionItemSearch
                              input-id="autosuggest__input"
                              @selected="onSelectedItem"
                            />
                          </b-col>
                          <b-col md="3">
                            <div class="d-flex justify-content-end">
                              <b-button
                                type="button"
                                variant="secondary"
                                size="sm"
                                @click="showImportModal"
                              >
                                <i
                                  style="font-size: 1rem"
                                  class="far fa-file-excel"
                                ></i
                                >Nhập từ Excel
                              </b-button>
                            </div>
                          </b-col>
                        </b-row>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="12">
                      <b-table
                        id="commission-category-items"
                        class="table-bordered table-hover col-md-12"
                        :fields="itemFields"
                        :items="mainModel.details"
                        :per-page="ITEM_PER_PAGE"
                        :current-page="currentItemPage"
                        show-empty
                        empty-text="Chưa có sản phẩm / danh mục nào"
                      >
                        <template v-slot:cell(type)="row">
                          <span>{{ itemTypeName(row.item.type) }}</span>
                        </template>
                        <template v-slot:cell(actions)="row">
                          <v-icon
                            small
                            class="text-danger"
                            @click="showRemoveItemAlert(row.item)"
                            v-b-tooltip
                            title="Xóa"
                            >mdi-delete</v-icon
                          >
                        </template>
                      </b-table>
                      <b-pagination
                        v-show="mainModel.details.length > ITEM_PER_PAGE"
                        v-model="currentItemPage"
                        :total-rows="mainModel.details.length"
                        :per-page="ITEM_PER_PAGE"
                        aria-controls="commission-category-items"
                        align="right"
                      ></b-pagination>
                    </b-col>
                  </b-row>
                </template>
              </b-container>
            </b-form>

            <!-- Modal nhập Excel: bước 1 chọn file → validate, bước 2 xem trước -->
            <b-modal
              ref="import-modal"
              hide-footer
              size="lg"
              title="Nhập sản phẩm / danh mục từ Excel"
            >
              <b-row>
                <b-col>
                  <span>
                    <b>Bước 1:</b> Chọn file Excel gồm 3 cột
                    <b>Loại</b>, <b>Mã</b>, <b>Tên</b>. Tối đa
                    {{ IMPORT_MAX_ROWS }} dòng mỗi lần nhập.
                  </span>
                  <div style="padding-top: 15px">
                    <b-form-file
                      placeholder="Hãy chọn một tập tin"
                      @change="selectFile"
                      v-model="file"
                      ref="file-input"
                      class="mb-2"
                    ></b-form-file>
                  </div>
                </b-col>
              </b-row>

              <b-row v-if="parsedRows.length">
                <b-col>
                  <span>
                    <b>Bước 2:</b> Kiểm tra dữ liệu ({{ parsedRows.length }}
                    dòng đọc được)
                  </span>
                  <div class="mt-2">
                    <b-button
                      variant="primary"
                      size="sm"
                      :disabled="isValidating"
                      @click="validateImport"
                    >
                      {{ isValidating ? 'Đang kiểm tra...' : 'Kiểm tra dữ liệu' }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>

              <!-- Danh sách lỗi: có lỗi thì KHÔNG cho lưu -->
              <b-container
                class="mt-4 pl-0 pr-0"
                v-if="importErrors.length > 0"
              >
                <b-alert show variant="danger" class="mb-2">
                  File có {{ importErrors.length }} dòng lỗi. Vui lòng sửa file
                  rồi tải lên lại — hệ thống không nhận dòng nào khi còn lỗi.
                </b-alert>
                <b-table
                  class="table-bordered table-hover"
                  :fields="errorFields"
                  :items="importErrors"
                  :per-page="ITEM_PER_PAGE"
                  :current-page="currentErrorPage"
                ></b-table>
                <b-pagination
                  v-show="importErrors.length > ITEM_PER_PAGE"
                  v-model="currentErrorPage"
                  :total-rows="importErrors.length"
                  :per-page="ITEM_PER_PAGE"
                  align="right"
                ></b-pagination>
              </b-container>

              <!-- Xem trước dữ liệu hợp lệ -->
              <b-container
                class="mt-4 pl-0 pr-0"
                v-if="validatedItems.length > 0 && !importErrors.length"
              >
                <b-alert show variant="success" class="mb-2">
                  Dữ liệu hợp lệ: {{ validatedItems.length }} dòng.
                </b-alert>
                <b-table
                  class="table-bordered table-hover"
                  :fields="importPreviewFields"
                  :items="validatedItems"
                  :per-page="ITEM_PER_PAGE"
                  :current-page="currentPreviewPage"
                >
                  <template v-slot:cell(type)="row">
                    <span>{{ itemTypeName(row.item.type) }}</span>
                  </template>
                </b-table>
                <b-pagination
                  v-show="validatedItems.length > ITEM_PER_PAGE"
                  v-model="currentPreviewPage"
                  :total-rows="validatedItems.length"
                  :per-page="ITEM_PER_PAGE"
                  align="right"
                ></b-pagination>
              </b-container>

              <div class="mt-4">
                <b-button
                  style="width: 70px"
                  variant="primary"
                  size="sm"
                  :disabled="!canApplyImport"
                  @click="applyImport"
                  >Lưu</b-button
                >
                <b-button
                  style="margin-left: 10px; width: 70px"
                  variant="secondary"
                  size="sm"
                  @click="hideImportModal"
                  >Hủy</b-button
                >
              </div>
            </b-modal>
          </template>
          <template v-slot:foot>
            <b-container class="bv-example-row">
              <b-row>
                <b-button
                  style="width: 70px"
                  variant="primary"
                  size="sm"
                  :disabled="isSubmitting"
                  @click="onSubmit"
                  >Lưu</b-button
                >
                <b-button
                  style="margin-left: 10px; width: 70px"
                  variant="secondary"
                  size="sm"
                  @click="onBack"
                  >Hủy</b-button
                >
              </b-row>
            </b-container>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import xlsx from 'xlsx';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import {
  COMMISSION_CATEGORY_TYPE,
  COMMISSION_CATEGORY_TYPE_NAME,
  COMMISSION_CATEGORY_ITEM_TYPE,
  COMMISSION_CATEGORY_ITEM_TYPE_NAME,
  COMMISSION_IMPORT_MAX_ROWS,
  toOptionList,
} from './commission.constants';
import commissionReportMixin from './commissionReport.mixin';
import CommissionItemSearch from './CommissionItemSearch.vue';

const ITEM_PER_PAGE = 10;

export default {
  mixins: [validationMixin, commissionReportMixin],
  components: {
    KTCodePreview,
    CommissionItemSearch,
  },
  data() {
    return {
      ITEM_PER_PAGE,
      IMPORT_MAX_ROWS: COMMISSION_IMPORT_MAX_ROWS,
      isSubmitting: false,
      isValidating: false,
      mainModel: {
        id: null,
        name: '',
        description: '',
        type: COMMISSION_CATEGORY_TYPE.PRODUCT,
        details: [],
      },
      typeOptions: toOptionList(COMMISSION_CATEGORY_TYPE_NAME),
      currentItemPage: 1,
      currentErrorPage: 1,
      currentPreviewPage: 1,
      file: null,
      parsedRows: [],
      validatedItems: [],
      importErrors: [],
      itemFields: [
        {
          key: 'type',
          label: 'Loại',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
        },
        {
          key: 'code',
          label: 'Mã',
          thStyle: { fontWeight: 600, color: '#181c32', width: '25%' },
        },
        {
          key: 'name',
          label: 'Tên',
          thStyle: { fontWeight: 600, color: '#181c32', width: '50%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
      ],
      importPreviewFields: [
        { key: 'type', label: 'Loại' },
        { key: 'code', label: 'Mã' },
        { key: 'name', label: 'Tên' },
      ],
      errorFields: [
        { key: 'row', label: 'Dòng' },
        { key: 'message', label: 'Lỗi' },
      ],
    };
  },
  validations: {
    mainModel: {
      name: { required },
    },
  },
  computed: {
    isUpdate() {
      return Boolean(this.mainModel.id);
    },
    pageTitle() {
      return this.isUpdate
        ? 'Cập nhật nhóm hoa hồng'
        : 'Thêm mới nhóm hoa hồng';
    },
    isProductType() {
      return this.mainModel.type === COMMISSION_CATEGORY_TYPE.PRODUCT;
    },
    canApplyImport() {
      return this.validatedItems.length > 0 && this.importErrors.length === 0;
    },
  },
  mounted() {
    const id = this.$route.query.id;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Nhóm hoa hồng', route: '/commission/categories' },
      { title: id ? 'Cập nhật nhóm hoa hồng' : 'Thêm mới nhóm hoa hồng' },
    ]);
    if (id) {
      this.fetchCategory(id);
    }
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.mainModel[name];
      return $dirty ? !$error : null;
    },
    itemTypeName(type) {
      return COMMISSION_CATEGORY_ITEM_TYPE_NAME[type] || '';
    },
    fetchCategory(id) {
      ApiService.setHeader();
      ApiService.query(`commission-categories/${id}`)
        .then(({ data }) => {
          const category = data.data || {};
          this.mainModel.id = category.id;
          this.mainModel.name = category.name;
          this.mainModel.description = category.description;
          this.mainModel.type = category.type;
          this.mainModel.details = (category.items || []).map((item) => ({
            referenceId: item.referenceId,
            type: item.type,
            code: item.code,
            name: item.name,
          }));
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được nhóm hoa hồng!'),
          );
        });
    },
    onSelectedItem({ itemType: type, item: selected }) {
      if (this.isDuplicatedItem(type, selected.id)) {
        makeToastFaile('Sản phẩm / danh mục này đã có trong nhóm!');
        return;
      }
      this.mainModel.details.push({
        referenceId: selected.id,
        type,
        code: selected.code,
        name: selected.name,
      });
    },
    isDuplicatedItem(type, referenceId) {
      return this.mainModel.details.some(
        (item) =>
          item.type === type && String(item.referenceId) === String(referenceId),
      );
    },
    showRemoveItemAlert(item) {
      Swal.fire({
        title: 'Xóa khỏi nhóm hoa hồng',
        text: `Bạn có chắc muốn xóa "${item.name}" khỏi nhóm không?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'top',
      }).then((result) => {
        if (result.value) {
          this.mainModel.details = this.mainModel.details.filter(
            (detail) =>
              !(
                detail.type === item.type &&
                String(detail.referenceId) === String(item.referenceId)
              ),
          );
        }
      });
    },
    showImportModal() {
      this.resetImportState();
      this.$refs['import-modal'].show();
    },
    hideImportModal() {
      this.$refs['import-modal'].hide();
    },
    resetImportState() {
      this.file = null;
      this.parsedRows = [];
      this.validatedItems = [];
      this.importErrors = [];
      this.currentErrorPage = 1;
      this.currentPreviewPage = 1;
    },
    selectFile(event) {
      this.parsedRows = [];
      this.validatedItems = [];
      this.importErrors = [];
      const selectedFile = event.target.files[0];
      if (!selectedFile) {
        return;
      }
      if (!/\.(xls|xlsx)$/.test(selectedFile.name.toLowerCase())) {
        this.file = null;
        this.$refs['file-input'].reset();
        makeToastFaile(
          'Định dạng file không hợp lệ. Vui lòng chọn file .xls hoặc .xlsx',
        );
        return;
      }
      const fileReader = new FileReader();
      fileReader.onload = (ev) => {
        try {
          const workbook = xlsx.read(ev.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
            blankRows: false,
          });
          if (rows.length > COMMISSION_IMPORT_MAX_ROWS) {
            makeToastFaile(
              `File vượt quá ${COMMISSION_IMPORT_MAX_ROWS} dòng cho phép mỗi lần nhập.`,
            );
            return;
          }
          this.parsedRows = rows.map((row, index) => ({
            row: index + 2, // dòng 1 là tiêu đề trong file Excel
            type:
              row['Loại'] === COMMISSION_CATEGORY_ITEM_TYPE_NAME[
                COMMISSION_CATEGORY_ITEM_TYPE.CATEGORY
              ]
                ? COMMISSION_CATEGORY_ITEM_TYPE.CATEGORY
                : COMMISSION_CATEGORY_ITEM_TYPE.PRODUCT,
            code: row['Mã'] ? String(row['Mã']).trim() : '',
            name: row['Tên'] ? String(row['Tên']).trim() : '',
          }));
        } catch (error) {
          makeToastFaile('Không đọc được nội dung file Excel!');
        }
      };
      fileReader.readAsBinaryString(selectedFile);
    },
    validateImport() {
      if (!this.parsedRows.length) {
        makeToastFaile('Chưa có dữ liệu để kiểm tra!');
        return;
      }
      this.isValidating = true;
      this.validatedItems = [];
      this.importErrors = [];
      ApiService.setHeader();
      ApiService.post('commission-categories/validate-import', {
        items: this.parsedRows,
      })
        .then(({ data }) => {
          const result = data.data || {};
          this.importErrors = result.errors || [];
          this.validatedItems = result.validItems || [];
          if (this.importErrors.length) {
            makeToastFaile(
              `File có ${this.importErrors.length} dòng lỗi, chưa thể lưu.`,
            );
          }
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Kiểm tra dữ liệu thất bại!'),
          );
        })
        .finally(() => {
          this.isValidating = false;
        });
    },
    applyImport() {
      if (!this.canApplyImport) {
        return;
      }
      let duplicated = 0;
      this.validatedItems.forEach((item) => {
        if (this.isDuplicatedItem(item.type, item.referenceId)) {
          duplicated += 1;
          return;
        }
        this.mainModel.details.push({
          referenceId: item.referenceId,
          type: item.type,
          code: item.code,
          name: item.name,
        });
      });
      const added = this.validatedItems.length - duplicated;
      makeToastSuccess(
        duplicated
          ? `Đã thêm ${added} dòng, bỏ qua ${duplicated} dòng đã có trong nhóm.`
          : `Đã thêm ${added} dòng từ file Excel.`,
      );
      this.hideImportModal();
    },
    onSubmit() {
      this.$v.mainModel.$touch();
      if (this.$v.mainModel.$anyError) {
        return;
      }

      const payload = {
        name: this.mainModel.name.trim(),
        type: this.mainModel.type,
        description: this.mainModel.description,
        // Nhóm Trade-in / Shipping chỉ có tên và mô tả — không gửi item
        items: this.isProductType
          ? this.mainModel.details.map((item) => ({
              referenceId: item.referenceId,
              type: item.type,
            }))
          : [],
      };

      this.isSubmitting = true;
      ApiService.setHeader();
      const request = this.isUpdate
        ? ApiService.put(`commission-categories/${this.mainModel.id}`, payload)
        : ApiService.post('commission-categories', payload);

      request
        .then(({ data }) => {
          makeToastSuccess(
            data.message ||
              (this.isUpdate
                ? 'Cập nhật nhóm hoa hồng thành công!'
                : 'Tạo nhóm hoa hồng thành công!'),
          );
          this.$router.push({ name: 'commission-categories' });
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Lưu nhóm hoa hồng thất bại!'),
          );
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    onBack() {
      this.$router.push({ name: 'commission-categories' });
    },
  },
};
</script>

<style>
.autosuggest__results ul {
  list-style: none;
  cursor: pointer;
  margin-bottom: 0;
}

.autosuggest__results {
  position: absolute;
  background-color: #fff;
  z-index: 1;
  width: 100%;
  border: 1px solid #e0e0e0;
  overflow: auto;
  max-height: 200px;
}

.autosuggest__results-container {
  position: relative;
  width: 100%;
}

#autosuggest__input {
  display: block;
  width: 100%;
  padding: 0.55rem 0.75rem;
  font-size: 0.925rem;
  font-weight: 400;
  line-height: 1.35;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.28rem;
  height: calc(1.35em + 1.1rem + 2px);
}

#autosuggest__input:focus {
  outline: none !important;
  border: 1px solid #92c5f9;
}

.autosuggest__results-item:hover {
  background-color: #ddd;
}

.autosuggest__results ul li {
  padding: 8px;
}

.form-group label {
  font-weight: 500;
}
</style>
